import {
  IconCheck,
  IconChevronDown,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Modal } from "antd";
import { useTranslation } from "react-i18next";

const cn = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(" ");

export const DEFAULT_REPORT_PRESET_TEXTS = [
  "焊道不良",
  "焊道角長不足",
  "焊道焊冠不足",
  "孔直徑有誤",
  "孔距有誤",
  "板型式有誤",
  "板材方向顛倒",
  "開槽方向有誤",
  "開孔未開",
];

// export const MAX_REPORT_ITEMS = 5;

export type ScanMode = "multiple" | "single" | "all";

export interface WorkItemDialogValue {
  name: string;
  employees: string[];
  scanMode: ScanMode;
  reportTexts: string[];
}

interface DialogEmployee {
  id: string;
  name: string;
}

interface ReportItem {
  id: string;
  label: string;
  custom: boolean;
}

interface WorkItemDialogProps {
  open: boolean;
  mode?: "create" | "edit";
  employees: DialogEmployee[];
  initialValue?: WorkItemDialogValue | null;
  presetReportTexts?: string[];
  confirmLoading?: boolean;
  onClose: () => void;
  onSubmit: (value: WorkItemDialogValue) => void;
}

let idSeq = 0;
const nextId = () => `report-item-${++idSeq}`;

const WorkItemDialog: React.FC<WorkItemDialogProps> = ({
  open,
  mode = "create",
  employees,
  initialValue = null,
  presetReportTexts = DEFAULT_REPORT_PRESET_TEXTS,
  confirmLoading = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isEditing = mode === "edit";

  const [name, setName] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [scanMode, setScanMode] = useState<ScanMode>("multiple");

  const [items, setItems] = useState<ReportItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const firstFieldRef = useRef<HTMLInputElement>(null);
  const employeeRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const initialValueRef = useRef(initialValue);
  initialValueRef.current = initialValue;
  const presetsRef = useRef(presetReportTexts);
  presetsRef.current = presetReportTexts;

  useEffect(() => {
    if (!open) return;
    const iv = initialValueRef.current;
    setName(iv?.name ?? "");
    setSelectedEmployees(iv?.employees ?? []);
    setScanMode(iv?.scanMode ?? "multiple");
    setCustomInput("");
    setEditingId(null);
    setEditingValue("");
    setEmployeeOpen(false);

    const base: ReportItem[] = presetsRef.current.map((label) => ({
      id: nextId(),
      label,
      custom: false,
    }));
    const selected: string[] = [];
    const extra: ReportItem[] = [];
    (iv?.reportTexts ?? []).forEach((label) => {
      const found = base.find((b) => b.label === label);
      if (found) {
        selected.push(found.id);
      } else {
        const id = nextId();
        extra.push({ id, label, custom: true });
        selected.push(id);
      }
    });
    setItems([...base, ...extra]);
    setSelectedIds(selected);

    const timer = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (employeeRef.current && !employeeRef.current.contains(e.target as Node)) {
        setEmployeeOpen(false);
      }
    }
    if (employeeOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [employeeOpen]);

  useEffect(() => {
    if (editingId) editInputRef.current?.focus();
  }, [editingId]);

  const employeeLabel = useMemo(() => {
    if (selectedEmployees.length === 0)
      return t("workItemDialog.selectPersonnel") || "請選擇人員";
    const names = selectedEmployees.map(
      (id) => employees.find((emp) => emp.id === id)?.name || id,
    );
    if (names.length <= 2) return names.join("、");
    return `${names.slice(0, 2).join("、")} ${
      t("workItemDialog.morePersons", { count: names.length }) ||
      `等 ${names.length} 人`
    }`;
  }, [selectedEmployees, employees, t]);

  const toggleEmployee = (id: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
    );
  };

  const toggleItem = (id: string) => {
    if (editingId === id) return;
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((i) => i !== id);
      return [...prev, id];
    });
  };

  const addCustomItem = () => {
    const value = customInput.trim();
    if (!value) return;
    if (!items.some((i) => i.label === value)) {
      const id = nextId();
      setItems((prev) => [...prev, { id, label: value, custom: true }]);
      setSelectedIds((prev) => [...prev, id]);
    }
    setCustomInput("");
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setSelectedIds((prev) => prev.filter((i) => i !== id));
    if (editingId === id) setEditingId(null);
  };

  const startEdit = (item: ReportItem) => {
    setEditingId(item.id);
    setEditingValue(item.label);
  };

  const commitEdit = () => {
    if (!editingId) return;
    const value = editingValue.trim();
    if (value) {
      setItems((prev) =>
        prev.map((i) => (i.id === editingId ? { ...i, label: value } : i)),
      );
    }
    setEditingId(null);
    setEditingValue("");
  };

  const handleCustomKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      addCustomItem();
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commitEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setEditingId(null);
      setEditingValue("");
    }
  };

  const handleSubmit = () => {
    onSubmit({
      name,
      employees: selectedEmployees,
      scanMode,
      reportTexts: items
        .filter((i) => selectedIds.includes(i.id))
        .map((i) => i.label),
    });
  };

  const scanModeOptions: { value: ScanMode; label: string }[] = [
    {
      value: "multiple",
      label: t("workItemDialog.scanModeMultiple") || "複數",
    },
    { value: "single", label: t("workItemDialog.scanModeSingle") || "單支" },
    { value: "all", label: t("workItemDialog.scanModeAll") || "全部" },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={768}
      destroyOnClose
      styles={{
        content: { padding: 0, borderRadius: 16, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      <div className="flex max-h-[80vh] flex-col bg-white text-gray-900">
        <div className="flex items-start justify-between px-7 pb-2 pt-6">
          <h2 className="text-2xl font-bold tracking-tight">
            {isEditing
              ? t("workItemDialog.editTitle") || "編輯工項"
              : t("workItemDialog.createTitle") || "新增工項"}
          </h2>
          <button
            type="button"
            aria-label={t("workItemDialog.close") || "關閉"}
            onClick={onClose}
            className="-mr-2 -mt-1 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <IconX className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-7 py-4">
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
            <div className="space-y-6">
              <Field label={t("workItemDialog.workItemSelection") || "工項選擇"}>
                <input
                  ref={firstFieldRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={
                    t("workItemDialog.enterWorkItemName") || "請輸入工項名稱"
                  }
                  className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3.5 text-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25"
                />
              </Field>

              <Field
                label={t("workItemDialog.personnelSelection") || "人員選擇"}
                hint={t("workItemDialog.multiSelectHint") || "可複選"}
              >
                <div className="relative" ref={employeeRef}>
                  <button
                    type="button"
                    onClick={() => setEmployeeOpen((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={employeeOpen}
                    className={cn(
                      "flex h-11 w-full items-center justify-between rounded-xl border bg-white px-3.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500/25",
                      employeeOpen ? "border-blue-500" : "border-gray-300",
                      selectedEmployees.length === 0 && "text-gray-400",
                    )}
                  >
                    <span className="truncate">{employeeLabel}</span>
                    <IconChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 text-gray-400 transition-transform",
                        employeeOpen && "rotate-180",
                      )}
                    />
                  </button>

                  {employeeOpen && (
                    <div
                      role="listbox"
                      aria-multiselectable="true"
                      className="absolute z-20 mt-2 max-h-56 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg"
                    >
                      {employees.length === 0 && (
                        <div className="px-2.5 py-2 text-sm text-gray-400">
                          {t("workItemDialog.noPersonnel") || "無可選人員"}
                        </div>
                      )}
                      {employees.map((emp) => {
                        const active = selectedEmployees.includes(emp.id);
                        return (
                          <button
                            key={emp.id}
                            type="button"
                            role="option"
                            aria-selected={active}
                            onClick={() => toggleEmployee(emp.id)}
                            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-gray-100"
                          >
                            <span
                              className={cn(
                                "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                                active
                                  ? "border-blue-600 bg-blue-600 text-white"
                                  : "border-gray-300 bg-white",
                              )}
                            >
                              {active && <IconCheck className="h-3 w-3" />}
                            </span>
                            <span className="text-gray-900">{emp.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedEmployees.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {selectedEmployees.map((id) => {
                      const emp = employees.find((e) => e.id === id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-md bg-blue-50 py-0.5 pl-2 pr-1 text-xs font-medium text-blue-600"
                        >
                          {emp?.name || id}
                          <button
                            type="button"
                            aria-label={`${t("workItemDialog.remove") || "移除"} ${emp?.name || id}`}
                            onClick={() => toggleEmployee(id)}
                            className="rounded p-0.5 hover:bg-blue-100"
                          >
                            <IconX className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </Field>

              <Field label={t("workItemDialog.scanPassCount") || "掃描通過支數"}>
                <div className="inline-flex rounded-xl border border-gray-300 bg-gray-50 p-1">
                  {scanModeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setScanMode(opt.value)}
                      className={cn(
                        "min-w-16 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors",
                        scanMode === opt.value
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-900",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <div className="space-y-6">
              <Field
                label={t("workItemDialog.reportItems") || "回報項目"}
                hint={
                  t("workItemDialog.reportItemsHint") ||
                  "選擇作業人員需回報的項目（可複選，不限數量）"
                }
              >
                <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex flex-wrap gap-2">
                    {items.map((item) => {
                      const selected = selectedIds.includes(item.id);
                      const editing = editingId === item.id;

                      if (editing) {
                        return (
                          <span
                            key={item.id}
                            className="inline-flex items-center gap-1 rounded-lg border border-blue-500 bg-white px-2 py-1"
                          >
                            <input
                              ref={editInputRef}
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              onBlur={commitEdit}
                              className="w-28 bg-transparent text-sm outline-none"
                            />
                            <button
                              type="button"
                              aria-label={
                                t("workItemDialog.finishEdit") || "完成編輯"
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={commitEdit}
                              className="rounded p-0.5 text-blue-600 hover:bg-blue-50"
                            >
                              <IconCheck className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        );
                      }

                      return (
                        <span
                          key={item.id}
                          className={cn(
                            "group inline-flex items-center gap-1 rounded-lg border py-1 pl-3 text-sm transition-colors",
                            item.custom ? "pr-1.5" : "pr-3",
                            selected
                              ? "border-blue-600 bg-blue-50 text-blue-600"
                              : "border-gray-300 bg-white text-gray-900 hover:border-blue-400",
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => toggleItem(item.id)}
                            className="inline-flex items-center gap-1.5"
                          >
                            {selected ? (
                              <IconCheck className="h-3.5 w-3.5" />
                            ) : (
                              <IconPlus className="h-3.5 w-3.5 opacity-60" />
                            )}
                            {item.label}
                          </button>
                          {item.custom && (
                            <span className="flex items-center gap-0.5 pl-0.5">
                              <button
                                type="button"
                                aria-label={`${t("workItemDialog.edit") || "編輯"} ${item.label}`}
                                onClick={() => startEdit(item)}
                                className="rounded p-0.5 text-gray-400 opacity-0 [@media(pointer:coarse)]:p-1.5 transition-opacity hover:text-gray-900 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                              >
                                <IconPencil className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                aria-label={`${t("workItemDialog.delete") || "刪除"} ${item.label}`}
                                onClick={() => removeItem(item.id)}
                                className="rounded p-0.5 text-gray-400 opacity-0 [@media(pointer:coarse)]:p-1.5 transition-opacity hover:text-red-600 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                              >
                                <IconTrash className="h-3 w-3" />
                              </button>
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>

                  <div className="flex gap-2 pt-1">
                    <input
                      value={customInput}
                      onChange={(e) => setCustomInput(e.target.value)}
                      onKeyDown={handleCustomKeyDown}
                      placeholder={
                        t("workItemDialog.addCustomItem") || "新增自定義項目"
                      }
                      className="h-10 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                    />
                    <button
                      type="button"
                      onClick={addCustomItem}
                      disabled={!customInput.trim()}
                      className="inline-flex h-10 items-center gap-1 rounded-lg bg-blue-600 px-3.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <IconPlus className="h-4 w-4" />
                      {t("workItemDialog.add") || "新增"}
                    </button>
                  </div>

                  <p className="text-xs text-gray-500">
                    {t("workItemDialog.reportItemsNote") ||
                      "預設項目僅供選擇；自定義項目新增後可編輯或刪除。"}
                  </p>

                  <p className="text-xs text-blue-600">
                    {t("workItemDialog.itemsSelected", {
                      count: selectedIds.length,
                    }) || `已選 ${selectedIds.length} 個項目`}
                  </p>
                </div>
              </Field>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-7 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100"
          >
            {t("workItemDialog.cancel") || "取消"}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={confirmLoading}
            className="rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isEditing
              ? t("workItemDialog.saveChanges") || "儲存變更"
              : t("workItemDialog.confirmAdd") || "確定新增"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="space-y-2">
    <div className="flex items-baseline gap-2">
      <label className="text-sm font-medium text-gray-900">{label}</label>
      {hint && <span className="text-xs text-gray-500">{hint}</span>}
    </div>
    {children}
  </div>
);

export default WorkItemDialog;
